// Game view - wrapper for GameMap component with hook modal

import { css, html } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';

// Import child components
import '../game/game-map.js';

/**
 * Game view component
 * Wrapper for the game-map component that handles authentication,
 * adventure hooks, and info banner messages
 */
class GameView extends BaseComponent {
  static properties = {
    gameId: { type: String, attribute: 'game-id' },
    _currentUserId: { type: String, state: true },
    _hookText: { type: String, state: true },
    _hookImageURL: { type: String, state: true },
    _hookTitle: { type: String, state: true },
    _hookButtonText: { type: String, state: true },
    _hookDismissed: { type: Boolean, state: true },
    _dismissingHook: { type: Boolean, state: true },
    _adventureObjective: { type: String, state: true },
    _showScrollIndicator: { type: Boolean, state: true },
    _waitingRoomActive: { type: Boolean, state: true },
  };

  static styles = css`
    :host {
      display: block;
      height: 100%;
    }

    .game-view {
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 100%;
    }

    /* Adventure Hook Modal */
    .hook-modal-backdrop {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.7);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      backdrop-filter: blur(4px);
    }

    .hook-modal {
      background: rgba(0, 0, 0, 0.95);
      border: 1px solid #444;
      border-radius: 12px;
      max-width: 700px;
      max-height: 80vh;
      width: 90%;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    .hook-modal-header {
      padding: 16px 24px;
      border-bottom: 1px solid #444;
      text-align: center;
    }

    .hook-title {
      margin: 0;
      font-size: 1.5rem;
      font-weight: 600;
      color: #e5e5e5;
    }

    .hook-modal-content {
      flex: 1;
      overflow-y: auto;
      padding: 24px;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 20px;
    }

    .hook-image {
      max-width: 100%;
      max-height: 250px;
      border-radius: 8px;
      border: 1px solid #444;
      object-fit: cover;
    }

    .hook-text {
      color: #ccc;
      font-size: 1rem;
      line-height: 1.7;
      text-align: left;
      white-space: pre-wrap;
    }

    .scroll-indicator {
      text-align: right;
      padding: 8px 24px;
      color: #888;
      font-size: 0.85rem;
    }

    .hook-modal-footer {
      padding: 16px 24px;
      border-top: 1px solid #444;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      justify-content: center;
    }

    .hook-start-btn {
      background: #333;
      border: 1px solid #555;
      border-radius: 8px;
      color: #fff;
      font-size: 1rem;
      font-weight: 600;
      padding: 12px 32px;
      cursor: pointer;
      transition: all 0.2s;
    }

    .hook-start-btn:hover:not(:disabled) {
      background: #444;
      border-color: #666;
    }

    .hook-start-btn:active:not(:disabled) {
      background: #222;
      transform: translateY(1px);
    }

    .hook-start-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  `;

  constructor() {
    super();
    this.gameId = '';
    this._currentUserId = null;
    this._hookText = null;
    this._hookImageURL = null;
    this._hookTitle = null;
    this._hookButtonText = null;
    this._hookDismissed = false;
    this._dismissingHook = false;
    this._adventureObjective = null;
    this._showScrollIndicator = false;
  }

  get _showHookModal() {
    return this._hookText && !this._hookDismissed && !this._waitingRoomActive;
  }

  async connectedCallback() {
    await super.connectedCallback();
    this._initializeGame();
  }

  _saveViewState() {
    if (!this.gameId) return;
    const viewState = {};
    try {
      localStorage.setItem(`game-${this.gameId}-view-state`, JSON.stringify(viewState));
    } catch {
      // localStorage full - non-critical data, skip silently
    }
  }

  async _initializeGame() {
    if (!this.gameId) return;

    try {
      const user = /** @type {import('../../types/jsdoc-types.js').User} */ (await api.getMe());
      this._currentUserId = user.id;
    } catch (error) {
      console.error('Failed to initialize game:', error);
    }
  }

  _handleExitGame() {
    router.navigate('/');
  }

  _handleEndGame() {
    router.navigate('/games');
  }

  /** @param {CustomEvent} e */
  _handleSnapshotUpdate(e) {
    const snapshot = e.detail;

    // Update hook state from snapshot
    if (snapshot.hook) {
      this._hookText = snapshot.hook.text || null;
      this._hookImageURL = snapshot.hook.imageURL || null;
      this._hookTitle = snapshot.hook.title || null;
      this._hookButtonText = snapshot.hook.buttonText || null;
      this._hookDismissed = snapshot.hook.dismissed || false;
    } else {
      this._hookText = null;
      this._hookImageURL = null;
      this._hookTitle = null;
      this._hookButtonText = null;
      this._hookDismissed = false;
    }

    // Update objective from snapshot
    this._adventureObjective = snapshot.objective || null;

    // Update waiting room state
    this._waitingRoomActive = snapshot.waitingRoom?.isActive || false;
  }

  _checkScrollIndicator() {
    const el = this.shadowRoot?.querySelector('.hook-modal-content');
    if (!el) {
      this._showScrollIndicator = false;
      return;
    }
    const hasMoreContent = el.scrollHeight - el.scrollTop - el.clientHeight > 10;
    this._showScrollIndicator = hasMoreContent;
  }

  async _dismissHook() {
    if (this._dismissingHook) return;

    try {
      this._dismissingHook = true;

      // Get the game-map component and call its submit action
      const gameMap =
        /** @type {HTMLElement & {submitAction?: (action: {type: string, data?: object}) => Promise<void>}} */ (
          this.shadowRoot?.querySelector('game-map')
        );
      if (gameMap?.submitAction) {
        await gameMap.submitAction({
          type: 'hook:dismiss',
        });
      }
    } catch (error) {
      console.error('Failed to dismiss hook:', error);
      this._dismissingHook = false;
    }
  }

  render() {
    return html`
      <div class="game-view">
        ${
          this.gameId && this._currentUserId
            ? html`
              <game-map
                game-id="${this.gameId}"
                width="100%"
                height="100%"
                current-user-id="${this._currentUserId}"
                backend-url="${api.getBackendUrl()}"
                .adventureObjective="${this._adventureObjective || undefined}"
                @exit-game="${this._handleExitGame}"
                @end-game="${this._handleEndGame}"
                @snapshot-updated="${this._handleSnapshotUpdate}"
              ></game-map>
            `
            : ''
        }
        ${
          this._showHookModal
            ? html`
              <div class="hook-modal-backdrop">
                <div class="hook-modal">
                  ${
                    this._hookTitle
                      ? html`
                        <div class="hook-modal-header">
                          <h2 class="hook-title">${this._hookTitle}</h2>
                        </div>
                      `
                      : ''
                  }
                  <div class="hook-modal-content" @scroll="${this._checkScrollIndicator}">
                    ${
                      this._hookImageURL
                        ? html` <img src="${this._hookImageURL}" alt="Adventure Hook" class="hook-image" /> `
                        : ''
                    }
                    <div class="hook-text">${this._hookText}</div>
                  </div>
                  ${this._showScrollIndicator ? html` <div class="scroll-indicator">Scroll for more</div> ` : ''}
                  <div class="hook-modal-footer">
                    <button class="hook-start-btn" ?disabled="${this._dismissingHook}" @click="${this._dismissHook}">
                      ${this._dismissingHook ? 'Starting...' : this._hookButtonText || 'Start Adventure'}
                    </button>
                  </div>
                </div>
              </div>
            `
            : ''
        }
      </div>
    `;
  }
}

customElements.define('game-view', GameView);
